import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  listMyProducts, upsertProduct, deleteProduct,
  listCategories, getProductForEdit,
  type VendorProduct,
} from "@/lib/vendor.functions";
import { getMyDashboard } from "@/lib/profile.functions";
import { ImageUpload } from "@/components/ImageUpload";
import { cleanHtmlToText } from "@/lib/clean-html-text";


export const Route = createFileRoute("/_authenticated/moji-izdelki")({
  head: () => ({ meta: [{ title: "Moji izdelki — kupish.si" }] }),
  validateSearch: (s: Record<string, unknown>): { new?: number } => (s.new ? { new: 1 } : {}),
  component: Page,
});


type Editing = {
  id: string | null;
  title: string;
  price: number | null;
  sale_price: number | null;
  short_description: string;
  description: string;
  featured_image_url: string | null;
  gallery_urls: string[];
  in_stock: boolean;
  stock_quantity: number | null;
  status: "publish" | "draft" | "pending" | "private";
  category_ids: string[];
};

const MAX_GALLERY = 5;

const emptyEditing: Editing = {
  id: null, title: "", price: null, sale_price: null,
  short_description: "", description: "", featured_image_url: null, gallery_urls: [],
  in_stock: true, stock_quantity: null, status: "publish", category_ids: [],
};

function Page() {
  const qc = useQueryClient();
  const search = Route.useSearch();
  const fetchList = useServerFn(listMyProducts);
  const save = useServerFn(upsertProduct);
  const del = useServerFn(deleteProduct);
  const fetchCats = useServerFn(listCategories);
  const fetchEdit = useServerFn(getProductForEdit);
  const fetchDash = useServerFn(getMyDashboard);

  const { data, isLoading, error } = useQuery({ queryKey: ["my-products"], queryFn: () => fetchList() });
  const { data: categories } = useQuery({ queryKey: ["categories"], queryFn: () => fetchCats() });
  const { data: dash } = useQuery({ queryKey: ["dashboard-sub"], queryFn: () => fetchDash() });
  const isActiveMember = dash?.subscription?.status === "active" || dash?.subscription?.status === "trialing" || (dash?.roles ?? []).includes("admin");

  const [editing, setEditing] = useState<Editing | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (search.new && !editing) setEditing(emptyEditing);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.new]);


  async function startEdit(p: VendorProduct) {
    const res = await fetchEdit({ data: { id: p.id } });
    const pr = res.product;
    setEditing({
      id: p.id,
      title: pr?.title ?? "",
      price: pr?.price !== null && pr?.price !== undefined ? Number(pr.price) : null,
      sale_price: pr?.sale_price !== null && pr?.sale_price !== undefined ? Number(pr.sale_price) : null,
      short_description: "",
      description: (() => {
        const s = cleanHtmlToText(pr?.short_description ?? "", 500);
        const l = cleanHtmlToText(pr?.description ?? "", 10000);
        if (s && l) {
          // Če je krajši opis že vsebovan v daljšem, ga ne podvajamo.
          if (l.toLowerCase().includes(s.toLowerCase())) return l;
          if (s.toLowerCase().includes(l.toLowerCase())) return s;
          return `${s}\n\n${l}`.slice(0, 10000);
        }
        return s || l;
      })(),
      featured_image_url: pr?.featured_image_url ?? null,
      gallery_urls: ((pr as unknown as { gallery_urls: string[] | null })?.gallery_urls ?? []).slice(0, MAX_GALLERY),
      in_stock: pr?.in_stock ?? true,
      stock_quantity: pr?.stock_quantity ?? null,
      status: (pr?.status as Editing["status"]) ?? "publish",
      category_ids: res.category_ids,
    });
  }

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    if (!editing) return;
    setFormError(null);
    setSaving(true);
    try {
      await save({
        data: {
          id: editing.id,
          title: editing.title,
          price: editing.price,
          sale_price: editing.sale_price,
          short_description: editing.short_description || null,
          description: editing.description || null,
          featured_image_url: editing.featured_image_url,
          gallery_urls: editing.gallery_urls,
          in_stock: editing.in_stock,
          stock_quantity: editing.stock_quantity,
          status: editing.status,
          category_ids: editing.category_ids,
        },
      });
      setEditing(null);
      qc.invalidateQueries({ queryKey: ["my-products"] });
    } catch (err) {
      setFormError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function onDelete(id: string) {
    if (!confirm("Izbrišem ta izdelek?")) return;
    await del({ data: { id } });
    qc.invalidateQueries({ queryKey: ["my-products"] });
  }

  function toggleCat(id: string) {
    if (!editing) return;
    setEditing({
      ...editing,
      category_ids: editing.category_ids.includes(id)
        ? editing.category_ids.filter((c) => c !== id)
        : [...editing.category_ids, id].slice(0, 10),
    });
  }

  return (
    <section className="mx-auto max-w-5xl px-6 py-16">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="eyebrow">Trgovina</div>
          <h1 className="font-serif text-4xl md:text-5xl mt-3">Moji izdelki</h1>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link to="/dashboard" className="border border-border px-4 py-2 text-sm hover:bg-muted">← Nadzorna plošča</Link>
          <button onClick={() => setEditing(emptyEditing)} disabled={!isActiveMember}
            title={!isActiveMember ? "Za dodajanje izdelkov je potrebna aktivna naročnina." : undefined}
            className="bg-primary text-primary-foreground px-4 py-2 text-sm hover:opacity-90 disabled:opacity-60">
            + Nov izdelek
          </button>
        </div>
      </div>

      {!isActiveMember && (
        <div className="mt-8 border border-accent bg-accent/10 p-5 text-sm">
          <p className="font-medium text-accent">Aktivirajte naročnino</p>
          <p className="mt-2 text-muted-foreground">
            Dodajanje in urejanje izdelkov je omogočeno po plačilu letne najemnine (240 €).
          </p>
          <Link to="/placilo-najemnine" className="mt-3 inline-block bg-accent text-accent-foreground px-4 py-2 text-sm">Plačaj najemnino →</Link>
        </div>
      )}

      {isLoading && <div className="mt-12 text-muted-foreground">Nalagam…</div>}
      {error && <div className="mt-12 text-destructive">Napaka: {(error as Error).message}</div>}

      {data && data.length === 0 && !editing && (
        <div className="mt-12 border border-dashed border-border p-12 text-center">
          <p className="text-muted-foreground">Še nimate izdelkov.</p>
        </div>
      )}

      {data && data.length > 0 && (
        <div className="mt-10 border border-border divide-y divide-border">
          {data.map((p) => <ProductRow key={p.id} p={p} startEdit={startEdit} onDelete={onDelete} />)}
        </div>
      )}


      {editing && (
        <div className="fixed inset-0 bg-background/90 z-50 overflow-y-auto" onClick={() => setEditing(null)}>
          <div className="mx-auto max-w-2xl my-4 bg-background border border-border p-4 sm:my-10 sm:p-8" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-serif text-3xl">{editing.id ? "Uredi izdelek" : "Nov izdelek"}</h2>
            <form className="mt-8 space-y-5" onSubmit={onSave}>
              <div className="grid grid-cols-1 gap-4 items-start sm:grid-cols-[200px_1fr]">
                <ImageUpload value={editing.featured_image_url}
                  onChange={(v) => setEditing({ ...editing, featured_image_url: v })}
                  folder="products" label="Glavna slika izdelka"
                  alt={editing.title ? `${editing.title} – ročno izdelano` : "Slika izdelka"} />
                <div>
                  <label className="text-xs eyebrow">Dodatne slike (do {MAX_GALLERY})</label>
                  <div className="mt-2 grid grid-cols-3 gap-2">
                    {editing.gallery_urls.map((url, i) => (
                      <div key={i} className="aspect-square w-full">
                        <ImageUpload
                          value={url}
                          onChange={(v) => {
                            const next = [...editing.gallery_urls];
                            if (v) next[i] = v;
                            else next.splice(i, 1);
                            setEditing({ ...editing, gallery_urls: next });
                          }}
                          folder="products"
                          label={`Slika ${i + 1}`}
                          alt={editing.title ? `${editing.title} – slika ${i + 1}` : `Dodatna slika ${i + 1}`}
                        />
                      </div>
                    ))}
                    {editing.gallery_urls.length < MAX_GALLERY && (
                      <div className="aspect-square w-full">
                        <ImageUpload
                          value={null}
                          onChange={(v) => {
                            if (!v) return;
                            setEditing({ ...editing, gallery_urls: [...editing.gallery_urls, v].slice(0, MAX_GALLERY) });
                          }}
                          folder="products"
                          label="Dodaj"
                          alt={editing.title ? `${editing.title} – nova slika` : "Nova slika"}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label className="text-xs eyebrow">Naslov izdelka *</label>
                <input required minLength={2} maxLength={80} value={editing.title}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value.slice(0, 80) })}
                  className="mt-2 w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-foreground" />
                <div className={`text-xs mt-1 ${editing.title.length > 40 ? "text-accent" : "text-muted-foreground"}`}>
                  {editing.title.length}/80 znakov{editing.title.length > 40 ? " – priporočamo do 40 znakov, da se naslov prikaže v eni vrstici" : " (priporočeno do 40 znakov za prikaz v eni vrstici)"}
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="text-xs eyebrow">Cena (EUR)</label>
                  <input type="number" step="0.01" min="0" value={editing.price ?? ""}
                    onChange={(e) => setEditing({ ...editing, price: e.target.value ? Number(e.target.value) : null })}
                    className="mt-2 w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-foreground" />
                </div>
                <div>
                  <label className="text-xs eyebrow">Akcijska cena (znižana)</label>
                  <input type="number" step="0.01" min="0" value={editing.sale_price ?? ""}
                    onChange={(e) => setEditing({ ...editing, sale_price: e.target.value ? Number(e.target.value) : null })}
                    className="mt-2 w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-foreground" />
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    Vpišite le, če izdelek znižujete. Mora biti nižja od redne cene — v trgovini se prikaže znižana cena, redna pa prečrtana. Če akcije ni, pustite polje prazno.
                  </p>
                </div>
              </div>
              <div>
                <label className="text-xs eyebrow">Opis izdelka</label>
                <textarea rows={5} maxLength={10000} value={editing.description}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  className="mt-2 w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-foreground" />
              </div>

              <div>
                <label className="text-xs eyebrow">Kategorije (do 10)</label>
                <div className="mt-2 flex flex-wrap gap-2 max-h-48 overflow-y-auto border border-border p-3">
                  {(categories ?? []).map((c) => {
                    const active = editing.category_ids.includes(c.id);
                    return (
                      <button key={c.id} type="button" onClick={() => toggleCat(c.id)}
                        className={`px-3 py-1 text-xs border ${active ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}>
                        {c.name}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div>
                  <label className="text-xs eyebrow">Zaloga</label>
                  <input type="number" min="0" value={editing.stock_quantity ?? ""}
                    onChange={(e) => setEditing({ ...editing, stock_quantity: e.target.value ? Number(e.target.value) : null })}
                    className="mt-2 w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-foreground" />
                </div>
                <div>
                  <label className="text-xs eyebrow">Na zalogi</label>
                  <select value={editing.in_stock ? "1" : "0"}
                    onChange={(e) => setEditing({ ...editing, in_stock: e.target.value === "1" })}
                    className="mt-2 w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-foreground">
                    <option value="1">Da</option>
                    <option value="0">Ne</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs eyebrow">Status</label>
                  <select value={editing.status}
                    onChange={(e) => setEditing({ ...editing, status: e.target.value as Editing["status"] })}
                    className="mt-2 w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-foreground">
                    <option value="publish">Objavljen</option>
                    <option value="draft">Osnutek</option>
                    <option value="pending">V pregledu</option>
                    <option value="private">Zasebno</option>
                  </select>
                </div>
              </div>
              {formError && <div className="text-sm text-destructive">{formError}</div>}
              <div className="flex flex-col gap-3 pt-2 sm:flex-row">
                <button type="submit" disabled={saving}
                  className="w-full bg-primary text-primary-foreground px-5 py-3 text-sm hover:opacity-90 disabled:opacity-60 sm:w-auto">
                  {saving ? "Shranjujem…" : "Shrani"}
                </button>
                <button type="button" onClick={() => setEditing(null)}
                  className="w-full border border-border px-5 py-3 text-sm hover:bg-muted sm:w-auto">
                  Prekliči
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}

function ProductRow({
  p,
  startEdit,
  onDelete,
}: {
  p: VendorProduct;
  startEdit: (p: VendorProduct) => void;
  onDelete: (id: string) => void;
}) {
  const [imgFailed, setImgFailed] = useState(false);
  const noImage = !p.featured_image_url || imgFailed;
  return (
    <div className="flex flex-wrap items-center gap-3 p-4 sm:flex-nowrap sm:gap-4">
      {noImage ? (
        <div className="w-16 h-16 bg-muted border border-dashed border-destructive flex items-center justify-center text-destructive text-[10px] text-center leading-tight px-1">
          Ni slike
        </div>
      ) : (
        <img
          src={p.featured_image_url as string}
          alt=""
          onError={() => setImgFailed(true)}
          className="w-16 h-16 object-cover"
        />
      )}
      <div className="min-w-0 flex-1 basis-[60%]">
        <div className="font-serif text-lg truncate uppercase">{p.title}</div>
        <div className="text-sm text-muted-foreground">
          {p.price ? <span className="text-accent">{p.price} {p.currency.toUpperCase()}</span> : "—"} · {p.status} · {p.in_stock ? "na zalogi" : "ni na zalogi"}
          {p.vendor_name && <> · <span className="text-foreground">{p.vendor_name}</span></>}
        </div>
        {noImage && (
          <div className="mt-1 inline-block border border-destructive bg-destructive/10 text-destructive text-xs px-2 py-0.5">
            ⚠ Naložite novo sliko — izdelek brez slike ni prikazan v trgovini.
          </div>
        )}
      </div>
      <button onClick={() => startEdit(p)} className="text-sm underline underline-offset-4 hover:text-accent">Uredi</button>
      <button onClick={() => onDelete(p.id)} className="text-sm text-destructive hover:underline">Izbriši</button>
    </div>
  );
}
